import { useState, type Dispatch, type SetStateAction } from "react";

import type { Language } from "@rootreeweb/multi-language";
import { getLocalizedField } from "@rootreeweb/multi-language";

import type {
	FAQCategoryWithQuestions,
	FAQQuestion,
	InteractiveFAQProps,
} from "../types.js";

type UseSearchResult = {
	current: FAQCategoryWithQuestions[] | FAQQuestion[];
	search: string;
	setSearch: Dispatch<SetStateAction<string>>;
};

/**
 * Find the FAQ questions that match the search term.
 *
 * @param categories The categories to search.
 * @param searchTerm The search term.
 * @param lang The site language.
 * @returns The matching questions.
 */
export function getSearchMatches(
	categories: FAQCategoryWithQuestions[],
	searchTerm: string,
	lang: Language,
): FAQQuestion[] {
	return categories
		.map((c) => c.questions.filter((q) => isMatch(q, searchTerm, lang)))
		.flat();
}

/**
 * Hook that allows for searching FAQ questions.
 *
 * @param props The properties from the FAQ component.
 * @returns The filtered results, search term, and callback for updating the
 *   search term.
 */
export function useSearch(props: InteractiveFAQProps): UseSearchResult {
	const [search, setSearch] = useState("");
	const toDisplay = search
		? getSearchMatches(props.categories, search, props.lang)
		: props.categories;
	return { current: toDisplay, search, setSearch };
}

/**
 * Check if an FAQ question matches the search term.
 *
 * @param item The item to check.
 * @param searchTerm The search term.
 * @param lang The language.
 * @returns True if the item matches the search term.
 */
function isMatch(
	item: FAQQuestion,
	searchTerm: string,
	lang: Language,
): boolean {
	return (
		getLocalizedField<string>(item, "question_", lang)
			.toLowerCase()
			.includes(searchTerm.toLowerCase()) ||
		getLocalizedField<string>(item, "answer_", lang)
			.toLowerCase()
			.includes(searchTerm.toLowerCase())
	);
}
